import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import InputDatePicker from "../../../../EvaluationNew/components/InputDatePicker";
import FormSignatureSections from "../FormSignatureSections";
import useFormSignatures from "../useFormSignatures";
import {
  FORM_FLAG_MESSAGES,
  CheckboxGrid,
  ExclusiveChoiceGrid,
  Field,
  FormActionBar,
  FormIdentityStrip,
  FormLoadingSkeleton,
  FormMetaRow,
  FormPageHeader,
  LockedReasonBanner,
  OfficeCommentsNote,
  OfficeReviewSection,
  SHEET,
  Section,
  SubHeading,
  fieldClass,
  textareaClass,
} from "../formLayout";
import {
  createPatientClinicalForm,
  getOtSupVisitForm,
  updateOtSupVisitForm,
} from "../../../../../services/patientService";
import {
  FileCheckIcon,
  HeartPulseIcon,
  MedicalNotesIcon,
  PhoneIcon,
  ShieldCheckIcon,
} from "../../../../../Icons";
import { displayDateUS, formatDateForInput } from "../../../../../utils/dateUtils";
import { PATIENT_PAGE_SHELL } from "../../../patientPageLayout";
import { saveDedicatedForm } from "../clinicalFormDraft";
import {
  draftCaregiverIdentity,
  useFormCaregiverPicker,
} from "../formCaregiver";
import { buildClinicalFormPermissions } from "../clinicalFormPermissions";
import { useClinicalCosignPermissions } from "../hooks/useClinicalCosignPermissions";
import {
  applyDisciplineFormPermissions,
  resolveFormActorRole,
  OT_FORM_EDITOR_ROLES,
} from "../disciplineFormAccess";
import {
  ADL_PERFORMANCE_OPTIONS,
  COGNITIVE_STATUS_OPTIONS,
  COMMUNICATION_METHOD_OPTIONS,
  COMMUNICATION_WITH_OPTIONS,
  FUNCTIONAL_STATUS_OPTIONS,
  HOME_ENVIRONMENT_OPTIONS,
  PARTICIPANT_OPTIONS,
  OT_SUP_ARRAY_KEYS,
  REQUIREMENTS_CHECKLIST_OPTIONS,
  SAFETY_AWARENESS_OPTIONS,
  SUPERVISORY_VISIT_TYPE_OPTIONS,
  VISIT_LOCATION_OPTIONS,
  YES_NO_OPTIONS,
  coerceExclusiveChoice,
  emptyOtSupVisitPayload,
  toggleInList,
  today,
} from "./otSupVisitContent";
import {
  assertOtVisitMetaForFlag,
  buildOtVisitMetaProps,
  pickOtVisitMeta,
} from "./otVisitCommon";
import OtNotesCommonHeader from "./OtNotesCommonHeader";
import {
  normalizeOtCommonHeaderState,
  resolveOtNotesCommonHeader,
  withNormalizedOtCommonHeader,
} from "./otNotesCommonHeaderUtils";

const VISIT_DATE_MAX = new Date().toISOString().slice(0, 10);

const emptyValues = () => ({
  ...emptyOtSupVisitPayload(),
  visitDate: today(),
  clinicianSignatureName: "",
  patientSignatureName: "",
  reviewComments: "",
});

const DateFieldInput = ({ name, value, canEdit, printMode, onChange, max }) => {
  if (printMode) {
    return (
      <div className={`${fieldClass} flex items-center`}>
        {displayDateUS(value) || "—"}
      </div>
    );
  }
  return (
    <InputDatePicker
      name={name}
      formatSeparator="/"
      allowManualEntry
      disabled={!canEdit}
      value={formatDateForInput(value) || ""}
      onChange={(event) => onChange(event.target.value)}
      max={max}
      className="w-full"
      inputClassName={`${fieldClass} bg-white`}
    />
  );
};

DateFieldInput.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  canEdit: PropTypes.bool,
  printMode: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  max: PropTypes.string,
};

const OtSupVisitForm = ({
  patient,
  clinicalForm,
  draft = null,
  onBack,
  onCreated,
  printMode = false,
  onReady,
}) => {
  const patientId = clinicalForm.patientId;
  const formId = clinicalForm.id || null;
  const isDraft = !formId;

  const [form, setForm] = useState(null);
  const [values, setValues] = useState(emptyValues);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const signatures = useFormSignatures();
  const { reset: resetSignatures } = signatures;

  const currentUser = useMemo(
    () => JSON.parse(localStorage.getItem("currentUser") || "{}"),
    [],
  );
  const role = resolveFormActorRole(currentUser);

  const patientName = useMemo(
    () => [patient?.lastName, patient?.firstName].filter(Boolean).join(", "),
    [patient?.lastName, patient?.firstName],
  );

  const hydrate = useCallback(
    (data) => {
      setForm(data);
      const base = emptyOtSupVisitPayload();
      const loadedPayload =
        data.payload && typeof data.payload === "object" && !Array.isArray(data.payload)
          ? data.payload
          : {};

      const merged = {
        ...base,
        ...loadedPayload,
        ...pickOtVisitMeta(loadedPayload, data),
        visitDate: data.visitDate || loadedPayload.visitDate || today(),
        commonHeader: normalizeOtCommonHeaderState(
          loadedPayload.commonHeader || base.commonHeader,
        ),
        supervisoryVisitType: coerceExclusiveChoice(
          loadedPayload.supervisoryVisitType ?? base.supervisoryVisitType,
        ),
        visitLocation: coerceExclusiveChoice(
          loadedPayload.visitLocation ?? base.visitLocation,
        ),
        adlPerformance: coerceExclusiveChoice(
          loadedPayload.adlPerformance ?? base.adlPerformance,
        ),
        homeEnvironment: coerceExclusiveChoice(
          loadedPayload.homeEnvironment ?? base.homeEnvironment,
        ),
        functionalStatus: coerceExclusiveChoice(
          loadedPayload.functionalStatus ??
            loadedPayload.functionalMobility ??
            base.functionalStatus,
        ),
        safetyAwareness: coerceExclusiveChoice(
          loadedPayload.safetyAwareness ?? base.safetyAwareness,
        ),
        cognitiveStatus: coerceExclusiveChoice(
          loadedPayload.cognitiveStatus ?? base.cognitiveStatus,
        ),
        communicationMethodOther:
          loadedPayload.communicationMethodOther ||
          loadedPayload.communicationOther ||
          "",
        patientSignatureName:
          data.patientSignatureName || data.patientName || patientName,
        clinicianSignatureName: data.clinicianSignatureName || "",
        reviewComments: data.reviewComments || "",
      };

      OT_SUP_ARRAY_KEYS.forEach((key) => {
        merged[key] = Array.isArray(loadedPayload[key])
          ? loadedPayload[key].map(String)
          : Array.isArray(base[key])
            ? base[key]
            : [];
      });

      // Migrate legacy nurse participant label → Supervising OT
      merged.participants = (merged.participants || []).map((p) =>
        p === "Supervising Nurse (RN/LPN)" ? "Supervising OT" : p,
      );

      setValues(merged);
      resetSignatures();
    },
    [patientName, resetSignatures],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const finish = () => {
      if (cancelled) return;
      setLoading(false);
      onReady?.();
    };

    const load = async () => {
      try {
        if (isDraft) {
          const commonHeader = await resolveOtNotesCommonHeader({
            patient,
            patientId,
            payload: {},
          });
          if (cancelled) return;
          hydrate({
            formName:
              clinicalForm.formName ||
              draft?.formName ||
              "Occupational Therapy Supervisory Visit",
            formType: clinicalForm.formType || draft?.formType || "ot_sup_visit",
            formSection: clinicalForm.formSection || draft?.formSection,
            status: "pending",
            patientName,
            patientDateOfBirth: patient?.dateOfBirth || null,
            ...draftCaregiverIdentity(currentUser, role),
            createdAt: new Date().toISOString(),
            permissions: buildClinicalFormPermissions("pending", role),
            visitDate: today(),
            clinicianSignatureName: "",
            patientSignatureName: patientName,
            reviewComments: "",
            payload: { ...emptyOtSupVisitPayload(), commonHeader },
          });
          return;
        }

        const res = await getOtSupVisitForm(patientId, formId);
        if (cancelled) return;
        const loaded = res.result?.form || res.form;
        const existingPayload =
          loaded.payload && typeof loaded.payload === "object"
            ? loaded.payload
            : {};
        const commonHeader = await resolveOtNotesCommonHeader({
          patient,
          patientId,
          payload: existingPayload,
        });
        if (cancelled) return;
        hydrate({
          ...loaded,
          payload: { ...existingPayload, commonHeader },
        });
      } catch (err) {
        if (cancelled) return;
        toast.error(err.message || "Failed to load form");
        onBack();
      } finally {
        finish();
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, formId, isDraft]);

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const toggleListValue = (key, item) => {
    setValues((prev) => ({
      ...prev,
      [key]: toggleInList(prev[key], item),
    }));
  };

  const { permissions: basePermissions } = useClinicalCosignPermissions({
    status: form?.status || "pending",
    role,
    actorUserId: currentUser?.id ? Number(currentUser.id) : null,
    formFilledByUserId:
      form?.filledBy ||
      clinicalForm?.filledBy ||
      (currentUser?.id ? Number(currentUser.id) : null),
    formFilledByRole: form?.filledByRole || clinicalForm?.filledByRole || role,
    serverPermissions: form?.permissions,
  });

  const permissions = useMemo(
    () =>
      applyDisciplineFormPermissions(
        basePermissions,
        role,
        OT_FORM_EDITOR_ROLES,
        "View only — only OT/OTA can edit this form.",
      ),
    [basePermissions, role],
  );

  const canEdit = !printMode && Boolean(permissions.canEdit);
  const isReviewer = Boolean(permissions.canReview);
  const canSign = !printMode && Boolean(permissions.canSignSignatures);

  const {
    caregiverSlot,
    filledByUserId,
    assertCaregiverForCreate,
  } = useFormCaregiverPicker({
    patientId,
    formType:
      form?.formType || draft?.formType || clinicalForm?.formType || "ot_sup_visit",
    role,
    canEdit,
    preferredStaffId: draft?.visit?.staffId ?? null,
    currentFilledByUserId:
      form?.filledBy || clinicalForm?.filledBy || null,
  });

  const handleClinicianSign = (signature, signerName) => {
    signatures.handleClinicianSign(signature);
    setValues((prev) => ({
      ...prev,
      clinicianSignatureName: signerName || prev.clinicianSignatureName,
      clinicianSignedDate: prev.clinicianSignedDate || today(),
    }));
  };

  const buildPayload = (flag) => {
    const {
      clinicianSignatureName,
      patientSignatureName,
      reviewComments,
      visitDate,
      ...otFields
    } = values;

    const nestedPayload = withNormalizedOtCommonHeader({
      ...otFields,
      visitDate: visitDate ? formatDateForInput(visitDate) : "",
      clinicianSignedDate: values.clinicianSignedDate
        ? formatDateForInput(values.clinicianSignedDate)
        : "",
    });

    OT_SUP_ARRAY_KEYS.forEach((key) => {
      nestedPayload[key] = Array.isArray(values[key]) ? values[key] : [];
    });

    const payload = {
      flag,
      visitDate: visitDate ? formatDateForInput(visitDate) : null,
      billingCode: values.billingCode || "",
      timeIn: values.timeIn || "",
      timeOut: values.timeOut || "",
      payload: nestedPayload,
      clinicianSignatureName: clinicianSignatureName || "",
      patientSignatureName: patientSignatureName || "",
      reviewComments: reviewComments || "",
    };

    if (!canSign) return payload;
    return { ...payload, ...signatures.buildSignaturePayload() };
  };

  const submit = async (flag) => {
    if (flag === "to_be_corrected" && !values.reviewComments?.trim()) {
      toast.error("Add review comments before marking the form to be corrected");
      return;
    }
    const visitMetaError = assertOtVisitMetaForFlag(values, flag);
    if (visitMetaError) {
      toast.error(visitMetaError);
      return;
    }
    const caregiverError = assertCaregiverForCreate();
    if (caregiverError) {
      toast.error(caregiverError);
      return;
    }
    setSaving(flag);
    try {
      const { form: saved, createdClinicalFormId } = await saveDedicatedForm({
        patientId,
        clinicalForm,
        draft,
        flag,
        payload: buildPayload(flag),
        createClinicalForm: createPatientClinicalForm,
        updateDedicatedForm: updateOtSupVisitForm,
        filledByUserId,
        scheduleDate: formatDateForInput(values.visitDate) || today(),
      });
      hydrate(saved);
      toast.success(FORM_FLAG_MESSAGES[flag] || "Form updated");
      if (createdClinicalFormId) onCreated?.(createdClinicalFormId);
    } catch (err) {
      toast.error(err.message || "Failed to save form");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <FormLoadingSkeleton />;
  if (!form) return null;

  const signatureProps = signatures.sectionProps(form);
  const showVisitTypeOther = values.supervisoryVisitType === "Other";
  const showLocationOther = values.visitLocation === "Other";
  const showParticipantsOther = (values.participants || []).includes(
    "Other Staff",
  );
  const showMethodOther = (values.communicationMethod || []).includes("Other");
  const showFollowUpSpecify = values.followUpRequired === "Yes";

  return (
    <div className={PATIENT_PAGE_SHELL}>
      {!printMode && (
        <FormPageHeader
          patientName={patientName}
          mrn={patient?.mrn}
          status={form.status}
          onBack={onBack}
        />
      )}

      <div
        className={`${SHEET} bg-white rounded-xl border border-slate-200 shadow-sm px-5 sm:px-8 py-6 space-y-3`}
      >
        <header className="text-center pb-1">
          <h1 className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-800">
            Occupational Therapy Supervisory Visit
          </h1>
          <p className="mt-1 text-[11px] text-slate-500">
            Supervisory visit requirements (CMS 42 CFR §484.80)
          </p>
          <span className="mx-auto mt-2 block h-0.5 w-14 rounded-full bg-indigo-300" />
        </header>

        <FormIdentityStrip
          form={form}
          patientName={patientName || form.patientName}
          mrn={patient?.mrn}
          dateOfBirth={displayDateUS(
            patient?.dateOfBirth ||
              values.commonHeader?.dateOfBirth ||
              form.patientDateOfBirth,
          )}
          caregiverSlot={caregiverSlot}
          dateLabel="Visit Date"
          dateValue={displayDateUS(values.visitDate)}
          dateSlot={
            printMode ? null : (
              <div className="min-w-0">
                <div className="text-[7px] font-semibold uppercase tracking-wider text-slate-400">
                  Visit Date
                </div>
                <InputDatePicker
                  name="visitDate"
                  formatSeparator="/"
                  allowManualEntry
                  compact
                  disabled={!canEdit}
                  value={formatDateForInput(values.visitDate) || ""}
                  onChange={(event) => setValue("visitDate", event.target.value)}
                  max={VISIT_DATE_MAX}
                  className="mt-0.5 min-w-0"
                  inputClassName="w-full h-4 text-[10px] font-bold bg-transparent border-0 p-0 outline-none"
                />
              </div>
            )
          }
          visitMeta={buildOtVisitMetaProps({
            values,
            setValue,
            setValues,
            canEdit,
            printMode,
          })}
        />

        <FormMetaRow form={form} printMode={printMode} />
        {!printMode && <LockedReasonBanner reason={permissions.lockedReason} />}

        <OtNotesCommonHeader
          value={values.commonHeader}
          canEdit={canEdit}
          printMode={printMode}
          onChange={(next) => setValue("commonHeader", next)}
        />

        {/* 7. Visit Information */}
        <Section icon={MedicalNotesIcon} title="7. Visit Information">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div>
              <SubHeading>Type of Supervisory Visit (select one)</SubHeading>
              <ExclusiveChoiceGrid
                name="supervisoryVisitType"
                options={SUPERVISORY_VISIT_TYPE_OPTIONS}
                value={values.supervisoryVisitType || ""}
                canEdit={canEdit}
                onChange={(opt) => setValue("supervisoryVisitType", opt)}
                columns={1}
              />
              {showVisitTypeOther ? (
                <input
                  type="text"
                  className={`${fieldClass} mt-1.5`}
                  value={values.supervisoryVisitTypeOther || ""}
                  disabled={!canEdit}
                  placeholder="Specify other visit type…"
                  onChange={(e) =>
                    setValue("supervisoryVisitTypeOther", e.target.value)
                  }
                />
              ) : null}
            </div>
            <div>
              <SubHeading>Location of Visit</SubHeading>
              <ExclusiveChoiceGrid
                name="visitLocation"
                options={VISIT_LOCATION_OPTIONS}
                value={values.visitLocation || ""}
                canEdit={canEdit}
                onChange={(opt) => setValue("visitLocation", opt)}
                columns={1}
              />
              {showLocationOther ? (
                <input
                  type="text"
                  className={`${fieldClass} mt-1.5`}
                  value={values.visitLocationOther || ""}
                  disabled={!canEdit}
                  placeholder="Specify other location…"
                  onChange={(e) =>
                    setValue("visitLocationOther", e.target.value)
                  }
                />
              ) : null}
            </div>
            <div>
              <SubHeading>Participants</SubHeading>
              <CheckboxGrid
                options={PARTICIPANT_OPTIONS}
                values={values.participants}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("participants", opt)}
                columns={1}
              />
              {showParticipantsOther ? (
                <input
                  type="text"
                  className={`${fieldClass} mt-1.5`}
                  value={values.participantsOther || ""}
                  disabled={!canEdit}
                  placeholder="Other staff…"
                  onChange={(e) =>
                    setValue("participantsOther", e.target.value)
                  }
                />
              ) : null}
            </div>
          </div>
        </Section>

        {/* 8. CMS Requirements */}
        <Section
          icon={ShieldCheckIcon}
          title="8. Supervisory Visit Requirements (CMS 42 CFR §484.80)"
        >
          <p className="text-[11px] italic text-slate-500 mb-2">
            The supervising therapist must perform an on-site visit and follow
            these requirements.
          </p>
          <CheckboxGrid
            options={REQUIREMENTS_CHECKLIST_OPTIONS}
            values={values.requirementsChecklist}
            canEdit={canEdit}
            onToggle={(opt) => toggleListValue("requirementsChecklist", opt)}
            columns={2}
          />
        </Section>

        {/* 9. Clinical Assessment */}
        <Section icon={HeartPulseIcon} title="9. Clinical Assessment">
          <div className="space-y-3">
            <div>
              <SubHeading>ADL Performance</SubHeading>
              <ExclusiveChoiceGrid
                name="adlPerformance"
                options={ADL_PERFORMANCE_OPTIONS}
                value={values.adlPerformance || ""}
                canEdit={canEdit}
                onChange={(opt) => setValue("adlPerformance", opt)}
                columns={3}
              />
              <Field label="Comments">
                <textarea
                  rows={2}
                  className={textareaClass}
                  value={values.adlComments || ""}
                  disabled={!canEdit}
                  onChange={(e) => setValue("adlComments", e.target.value)}
                />
              </Field>
            </div>

            <div>
              <SubHeading>Home Environment / Adaptive Equipment Needs</SubHeading>
              <ExclusiveChoiceGrid
                name="homeEnvironment"
                options={HOME_ENVIRONMENT_OPTIONS}
                value={values.homeEnvironment || ""}
                canEdit={canEdit}
                onChange={(opt) => setValue("homeEnvironment", opt)}
                columns={3}
              />
              <Field label="Recommendations">
                <textarea
                  rows={2}
                  className={textareaClass}
                  value={values.homeEnvironmentRecommendations || ""}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("homeEnvironmentRecommendations", e.target.value)
                  }
                />
              </Field>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <SubHeading>Functional Status</SubHeading>
                <ExclusiveChoiceGrid
                  name="functionalStatus"
                  options={FUNCTIONAL_STATUS_OPTIONS}
                  value={values.functionalStatus || ""}
                  canEdit={canEdit}
                  onChange={(opt) => setValue("functionalStatus", opt)}
                  columns={1}
                />
              </div>
              <div>
                <SubHeading>Safety Awareness</SubHeading>
                <ExclusiveChoiceGrid
                  name="safetyAwareness"
                  options={SAFETY_AWARENESS_OPTIONS}
                  value={values.safetyAwareness || ""}
                  canEdit={canEdit}
                  onChange={(opt) => setValue("safetyAwareness", opt)}
                  columns={1}
                />
              </div>
              <div>
                <SubHeading>Cognitive Status</SubHeading>
                <ExclusiveChoiceGrid
                  name="cognitiveStatus"
                  options={COGNITIVE_STATUS_OPTIONS}
                  value={values.cognitiveStatus || ""}
                  canEdit={canEdit}
                  onChange={(opt) => setValue("cognitiveStatus", opt)}
                  columns={1}
                />
              </div>
            </div>

            <Field label="Cognitive Comments">
              <textarea
                rows={2}
                className={textareaClass}
                value={values.cognitiveComments || ""}
                disabled={!canEdit}
                onChange={(e) => setValue("cognitiveComments", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        {/* 10. Plan of Care Review */}
        <Section icon={FileCheckIcon} title="10. Plan of Care Review">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <SubHeading>Plan of Care Reviewed</SubHeading>
              <ExclusiveChoiceGrid
                name="planOfCareReviewed"
                options={YES_NO_OPTIONS}
                value={values.planOfCareReviewed || ""}
                canEdit={canEdit}
                onChange={(opt) => setValue("planOfCareReviewed", opt)}
                columns={2}
              />
            </div>
            <div>
              <SubHeading>Follow-up Required</SubHeading>
              <ExclusiveChoiceGrid
                name="followUpRequired"
                options={YES_NO_OPTIONS}
                value={values.followUpRequired || ""}
                canEdit={canEdit}
                onChange={(opt) => setValue("followUpRequired", opt)}
                columns={2}
              />
            </div>
            <div>
              <SubHeading>Plan of Care Remains Appropriate</SubHeading>
              <ExclusiveChoiceGrid
                name="planRemainsAppropriate"
                options={YES_NO_OPTIONS}
                value={values.planRemainsAppropriate || ""}
                canEdit={canEdit}
                onChange={(opt) => setValue("planRemainsAppropriate", opt)}
                columns={2}
              />
              <p className="mt-1 text-[10px] italic text-slate-400">
                (If No, see revisions below)
              </p>
            </div>
            {showFollowUpSpecify ? (
              <Field label="If Yes, specify">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.followUpSpecify || ""}
                  disabled={!canEdit}
                  onChange={(e) => setValue("followUpSpecify", e.target.value)}
                />
              </Field>
            ) : (
              <div />
            )}
          </div>
          <div className="mt-3">
            <Field label="Revisions to Plan of Care / New Orders">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.planRevisions || ""}
                disabled={!canEdit}
                onChange={(e) => setValue("planRevisions", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        {/* 11. Communication */}
        <Section icon={PhoneIcon} title="11. Communication">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="space-y-3">
              <div>
                <SubHeading>Communication with</SubHeading>
                <CheckboxGrid
                  options={COMMUNICATION_WITH_OPTIONS}
                  values={values.communicationWith}
                  canEdit={canEdit}
                  onToggle={(opt) => toggleListValue("communicationWith", opt)}
                  columns={2}
                />
              </div>
              <div>
                <SubHeading>Method</SubHeading>
                <CheckboxGrid
                  options={COMMUNICATION_METHOD_OPTIONS}
                  values={values.communicationMethod}
                  canEdit={canEdit}
                  onToggle={(opt) =>
                    toggleListValue("communicationMethod", opt)
                  }
                  columns={2}
                />
                {showMethodOther ? (
                  <input
                    type="text"
                    className={`${fieldClass} mt-1.5`}
                    value={values.communicationMethodOther || ""}
                    disabled={!canEdit}
                    placeholder="Other method…"
                    onChange={(e) =>
                      setValue("communicationMethodOther", e.target.value)
                    }
                  />
                ) : null}
              </div>
            </div>
            <Field label="Summary of Communication">
              <textarea
                rows={6}
                className={textareaClass}
                value={values.communicationSummary || ""}
                disabled={!canEdit}
                onChange={(e) =>
                  setValue("communicationSummary", e.target.value)
                }
              />
            </Field>
          </div>
        </Section>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="OT License #">
            <input
              type="text"
              className={fieldClass}
              value={values.clinicianLicense || ""}
              disabled={!canEdit}
              placeholder="OT-########"
              onChange={(e) => setValue("clinicianLicense", e.target.value)}
            />
          </Field>
          <Field label="Signature Date">
            <DateFieldInput
              name="clinicianSignedDate"
              value={values.clinicianSignedDate}
              canEdit={canEdit}
              printMode={printMode}
              onChange={(v) => setValue("clinicianSignedDate", v)}
            />
          </Field>
        </div>
        <Field label="Patient/Caregiver Relationship">
          <input
            type="text"
            className={fieldClass}
            value={values.patientRelationship || ""}
            disabled={!canEdit}
            placeholder="e.g., Self, Spouse, Daughter…"
            onChange={(e) => setValue("patientRelationship", e.target.value)}
          />
        </Field>

        <FormSignatureSections
          {...signatureProps}
          showPatientSection
          patientNameValue={values.patientSignatureName}
          onPatientNameChange={(v) => setValue("patientSignatureName", v)}
          clinicianNameValue={values.clinicianSignatureName}
          onClinicianSign={handleClinicianSign}
          canSign={canSign}
          showClinicianSection={!permissions.hideClinicalSignature}
          printMode={printMode}
          clinicianSectionTitle="Supervising Occupational Therapist"
          patientSectionTitle="Patient/Caregiver (Acknowledgment of Visit)"
        />

        {isReviewer && !printMode && (
          <OfficeReviewSection
            value={values.reviewComments}
            onChange={(v) => setValue("reviewComments", v)}
          />
        )}

        {(!isReviewer || printMode) && (
          <OfficeCommentsNote comments={form.reviewComments} />
        )}
      </div>

      {(permissions.canSendToOffice ||
        permissions.canToBeSign ||
        permissions.canReview ||
        permissions.canEdit) && (
        <FormActionBar
          permissions={permissions}
          status={form.status}
          canEdit={canEdit}
          saving={saving}
          onSubmit={submit}
          printMode={printMode}
        />
      )}
    </div>
  );
};

OtSupVisitForm.propTypes = {
  patient: PropTypes.object,
  clinicalForm: PropTypes.object.isRequired,
  draft: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
  printMode: PropTypes.bool,
  onReady: PropTypes.func,
};

export default OtSupVisitForm;
